import { getNumberWithOrdinal } from './numbers'
import { dayjs } from './dayjsExtension'

export const prettyMonths = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// Returns YYYY-MM
export const getDateNowFormatted = () => {
  const date = new Date(Date.now())
  return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}`
}

export const getSelectedValue = (monthYearFrom: string) => {
  const splitDate = monthYearFrom.split('-')
  const prettyMonth = prettyMonths[Number(splitDate[1]) - 1]
  return {
    value: [splitDate[0], splitDate[1]].join('-'),
    label: `${prettyMonth} ${splitDate[0]}`,
  }
}

/**
 * Returns short date+month string, i.e 10th September
 * @param date {Date} the date to use
 * @returns {string} formatted string, i.e 12th June
 */
export const getDateMonthWithOrdinal = (date: Date) => {
  const monthName = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(
    date
  ) // i.e September
  const dateWithOrdinal = getNumberWithOrdinal(date.getDate()) // i.e 10th
  return `${dateWithOrdinal} ${monthName}` // i.e 10th September
}

export const getDaysTillEnd = (endDate: string | null) => {
  let daysTillEnd
  if (endDate) {
    daysTillEnd = dayjs(endDate).endOf('day').diff(dayjs(), 'days')
  }
  return daysTillEnd
}

export const getFormattedEndDate = (endDate: string | null) => {
  if (endDate) {
    return dayjs(endDate).format('dddd Do MMM')
  }
  return ''
}

export const addDays = (date: Date | string, days: number) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export interface Timestamp {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export const calculateTimeElapsed = (timestamp: string): Timestamp => {
  const diff = Date.parse(timestamp) - Date.now()
  const timeObj = { days: 0, hours: 0, minutes: 0, seconds: 0 }
  if (diff > 0) {
    timeObj.days = Math.floor(diff / (1000 * 60 * 60 * 24))
    timeObj.hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
    timeObj.minutes = Math.floor((diff / 1000 / 60) % 60)
    timeObj.seconds = Math.floor((diff / 1000) % 60)
  }
  return timeObj
}

export const hasDayPassedSinceDate = (
  date: string | null | undefined,
  options?: { endOfDay?: boolean }
): boolean => {
  if (!date) return true

  if (options?.endOfDay) {
    return dayjs() > dayjs(date).add(1, 'days').endOf('day')
  }
  return dayjs() > dayjs(date).add(1, 'days')
}

export const hasHourPassedSinceDate = (
  date: string | null | undefined
): boolean => {
  if (!date) return false

  return dayjs(date).add(1, 'hours') > dayjs()
}

export const getDaysDiffFromToday = (
  date: string | null | undefined
): number => {
  if (!date) return Infinity

  return dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'days')
}

export const formatDate = (date?: Date | string | null, format?: string) =>
  date ? dayjs(date).format(format || 'DD/MM/YYYY') : ''

export const formatIsoDate = (date?: Date | string | null) =>
  date ? dayjs(date).toISOString() : ''

export const hasDatePassedSinceTarget = (
  date: string | null | undefined,
  targetDate: string | null | undefined
): boolean => {
  if (!date || !targetDate) return false

  return dayjs(date).isAfter(dayjs(targetDate))
}

export const isDateMoreThanTwoDaysAgo = (dateString: string) => {
  const inputDate = dayjs(dateString).startOf('day')
  const today = dayjs().startOf('day')
  const differenceInDays = today.diff(inputDate, 'day')

  return differenceInDays > 2
}

export const SMARTY_LONG_DATE_FORMAT = 'dddd D MMM YYYY' // f.e. Saturday 29 Jun 2024

export const SHORT_DAY_DATE_FORMAT = 'YYYY-MM-DD' // e.g. 2024-05-21
